import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faQuoteLeft } from "@fortawesome/free-solid-svg-icons";

const gradients = [
  "from-blue-500 to-cyan-500",
  "from-purple-500 to-pink-500",
  "from-green-500 to-teal-500",
  "from-orange-500 to-red-500",
  "from-indigo-500 to-purple-500",
  "from-teal-500 to-green-500",
];

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const SHEET_URL =
      "https://opensheet.elk.sh/1fZwqy4JsyM7Arv0BuM6AH_oBC54ipfbgcwOKbJQv0MI/Form Responses 1";

    fetch(SHEET_URL)
      .then((res) => res.json())
      .then((data) => {
        if (!Array.isArray(data)) {
          throw new Error("Invalid data format");
        }

        // ✅ Normalize sheet data once (VERY IMPORTANT)
        const normalized = data.map((item) => ({
          name: item["Full Name "] || "Anonymous",
          role: item["Your Role"] || "",
          company: item["Company / Project Name"] || "",
          rating: Number(item["How would you rate your experience?"] || 5),
          testimonial: item["Your Feedback / Review"] || "",
          approved: item["Approved"] === "TRUE" || item["Approved"] === true,
        }));

        // ✅ Filter approved only
        const approvedTestimonials = normalized
          .filter((item) => item.approved)
          .map((item, index) => ({
            ...item,
            gradient: gradients[index % gradients.length],
            image: `https://ui-avatars.com/api/?name=${encodeURIComponent(
              item.name,
            )}&background=random`,
          }));

        setTestimonials(approvedTestimonials);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load testimonials");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  return (
    <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] py-12 px-4 sm:px-6 lg:px-8 rounded-2xl shadow-2xl">
      <h2 className="text-4xl font-bold text-[#e2e8f0] pb-6 text-center relative orbitron">
        Testimonials
        <span className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-gradient-to-r from-transparent via-[#94a3b8] to-transparent"></span>
      </h2>

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="animate-pulse bg-slate-800/50 rounded-2xl p-6 h-64"
            />
          ))}
        </div>
      )}

      {/* Error */}
      {!isLoading && error && (
        <p className="text-center text-red-400">{error}</p>
      )}

      {/* Empty */}
      {!isLoading && !error && testimonials.length === 0 && (
        <p className="text-center text-gray-400">
          No testimonials available yet.
        </p>
      )}

      {/* Testimonials */}
      {!isLoading && !error && testimonials.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-2">
          {testimonials.map((t, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${t.gradient} p-0.5 rounded-2xl transition-all hover:scale-105 hover:shadow-lg`}
            >
              <div className="bg-[#0f172a] rounded-2xl p-6 h-full flex flex-col">
                <FontAwesomeIcon
                  icon={faQuoteLeft}
                  size="2x"
                  className="text-gray-400 opacity-70 mb-4"
                />

                <p className="text-gray-300 text-base leading-relaxed mb-6 flex-grow  italic">
                  “{t.testimonial}”
                </p>

                <div className="flex items-center mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <FontAwesomeIcon
                      key={i}
                      icon={faStar}
                      className="text-yellow-400 mr-1"
                      size="sm"
                    />
                  ))}
                </div>

                <div className="flex items-center pt-4 border-t border-slate-700/50">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-12 h-12 rounded-full mr-4 border-2 border-slate-600"
                    loading="lazy"
                  />
                  <div>
                    <h4 className="text-gray-200 font-bold">{t.name}</h4>
                    {t.role && (
                      <p className="text-gray-400 text-sm">{t.company}</p>
                    )}
                    {t.company && (
                      <p className="text-gray-500 text-xs">{t.role}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-center text-sm text-gray-500 mt-8">
        All testimonials are shared with client permission.
      </p>
    </div>
  );
};

export default Testimonials;
